import { z } from 'zod';
import { IAlternativeUpload } from '@printdeal/alternative-upload';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';

export interface FeedbackFormValues {
  feedbackType: string;
  attachments: IAlternativeUpload[];
  body: string;
  pageUrl: string;
}

export const AlternativeUploadProgressSchema = z.object({
  percentage: z.number().min(0).max(100),
  loaded: z.object({
    value: z.number().min(0),
    unit: z.string(),
  }).optional(),
  total: z.object({
    value: z.number().min(0),
    unit: z.string(),
  }).optional(),
});

// Zod schema for AlternativeUploadUrls
export const AlternativeUploadUrlsSchema = z.object({
  originalUrl: z.string().url().optional(),
  previewUrl: z.string().url().optional(),
  printUrl: z.string().url().optional(),
});

// Zod schema for IAlternativeUpload
export const IAlternativeUploadSchema = z.object({
  fileType: z.string().min(1),
  fileSize: z.string().min(1),
  uploadTitle: z.string().min(1),
  id: z.string().optional(),
  progress: AlternativeUploadProgressSchema.optional(),
  uploadTimestamp: z.string().optional(),
  urls: AlternativeUploadUrlsSchema.optional(),
});

export const feedbackFormSchema = z.object({
  feedbackType: z.string().min(1),
  attachments: z.array(IAlternativeUploadSchema).optional(),
  body: z.string().min(1).refine((val) => val.trim().length > 0),
});

// Default form values
export const getDefaultFormValues = (): FeedbackFormValues => ({
  feedbackType: '',
  attachments: [],
  body: '',
  pageUrl: '',
});

const Event = {
  feedbackModalOpen: 'feedback_modal_open',
  feedbackModalInteraction: 'feedback_modal_interaction',
  feedbackModalClose: 'feedback_modal_close',
  feedbackModalCompleted: 'feedback_modal_completed',
};

export const dispatchDataLayerEventOnModalOpen = () => {
  addDataLayerEntry(Event.feedbackModalOpen, {
    event: Event.feedbackModalOpen,
  });
};

export const dispatchDataLayerEventOnFieldInteraction = (fieldName: string) => {
  addDataLayerEntry(Event.feedbackModalInteraction, {
    event: Event.feedbackModalInteraction,
    field_interaction: fieldName,
  });
};

export type EventsForClosure =
  'modal_close_generic'
  | 'modal_close_by_button'
  | 'modal_close_after_success';
export const dispatchDataLayerEventOnModalClose = (eventName: EventsForClosure) => {
  addDataLayerEntry(Event.feedbackModalClose, {
    event: Event.feedbackModalClose,
    closureType: eventName,
  });
};

export const dispatchDataLayerEventOnFormCompleted = () => {
  addDataLayerEntry(Event.feedbackModalCompleted, {
    event: Event.feedbackModalCompleted,
  });
};
